import { Dispatch, SetStateAction, useState } from 'react'
import { Brand, I18NText, Position, State, Language } from 'src/types'
import { UseFormWatch } from 'react-hook-form'
import { usModelOfAtomsChecker } from 'src/logic'
import { genBaseCells, genColorCells } from 'src/logic/simulationSpecification/glove'
import { genEmbroideryCells } from 'src/logic/simulationSpecification/glove'
import { emojiChecker, isInvalidPersonalData } from 'src/logic'
import { getYellStoryOptionalEmbroideryCells } from 'src/features/yellStory/simulatedSpecification'

const getOriginalEmbroideryCells = (state: State, brand: Brand) => {
  switch (brand) {
    case 'yellStory':
      return getYellStoryOptionalEmbroideryCells(state)
    default:
      return []
  }
}

type CanStepFurther = {
  canSave: boolean
  canInputConsumer: boolean
  canClose: boolean
  existArribaFontNoPointColor: boolean
  existEmoji: boolean
  setIsSave: Dispatch<SetStateAction<boolean>>
}

type CompareEmailWatch = UseFormWatch<{
  mailAddress: string
  mailAddress2: string
}>

export const useCanStepFurther = (
  state: State,
  brand: Brand,
  position: Position,
  isCopied: boolean,
  t: I18NText,
  language: Language,
  watch: CompareEmailWatch
): CanStepFurther => {
  const [isSaved, setIsSave] = useState<boolean>(false)
  const watchMailAddress = watch('mailAddress')
  const watchMailAddress2 = watch('mailAddress2')

  const baseSettings = genBaseCells(state, brand, position, t, language)
  const colorSettings = genColorCells(position, state, brand, t, language)
  const existEmbroidery = state.embroideries.filter((e) => e.content.trim().length > 0).length > 0
  const embroiderySettings = existEmbroidery ? state.embroideries.map((e) => genEmbroideryCells(e, t, language)).flat() : []
  const optionalEmbroideryCells = getOriginalEmbroideryCells(state, brand)
  const existInvalidPersonal = isInvalidPersonalData(state.personal)

  const existUnselectedState = [...baseSettings, ...colorSettings, ...embroiderySettings, ...optionalEmbroideryCells].some(
    (y) => y.value === 'unselected'
  )
  const existKoreanCharacterInEmbroideries = state.embroideries.some((e) => e.content.charCodeAt(0) >= 50001 && !emojiChecker(e.content))
  const isInValidMailAddress = watchMailAddress !== watchMailAddress2 || [watchMailAddress, watchMailAddress2].some((x) => x.length === 0)
  const existArribaFontNoPointColor = state.embroideries.some((e) => e.typeFace.value === 'arriba' && !e.pointColor) // only YSR

  const existEmoji = state.embroideries.map((e) => e.content).some(emojiChecker) && !usModelOfAtomsChecker(state.baseModel.productNumber)
  const canInputConsumer = ![existUnselectedState, existKoreanCharacterInEmbroideries, existArribaFontNoPointColor].includes(true)
  const canSave = [existInvalidPersonal, isSaved, isInValidMailAddress, existEmoji].every((b) => b === false)
  const canClose = (isSaved && isCopied) || (!isSaved && !isCopied)

  return {
    canSave,
    canInputConsumer,
    canClose,
    existArribaFontNoPointColor,
    existEmoji,
    setIsSave
  }
}
